import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  Mutation,
  QueryLoadingState,
  QueryOptions,
  useMutation,
  useQuery,
} from '@globalconnect/query';
import { map, tap } from 'rxjs';
import { SelectSpanModeService } from '../../../../span-progress-app/service/map/span/select-span-mode/select-span-mode.service';
import { buildRolloutReportUrl } from '../../../../span-progress-app/span-progress-view/popup-content/popup-pdf.utils';
import { FeedbackAlertService } from '../../../feedback-alert/service/feedback-alert.service';
import {
  MULTI_SPAN_REPORT_CREATED,
  MULTI_SPAN_REPORT_UPDATED,
} from '../../../posthug-analytics/analytics-event-types';
import { AnalyticsService } from '../../../posthug-analytics/analytics.service';
import { RolloutProgressReportEntities } from '../entities';
import { rpReportKey, rpReportsKey } from '../entities/query-keys';
import { InspectionReportInitService } from '../initial-values/inspection-report-init.service';
import { RolloutProgressReportsApiService } from './api/rollout-progress-reports-api.service';

@Injectable({
  providedIn: 'root',
})
export class RolloutProgressReportsService {
  private readonly apiService = inject(RolloutProgressReportsApiService);
  private readonly initService = inject(InspectionReportInitService);
  private readonly selectSpanModeService = inject(SelectSpanModeService);
  private readonly feedbackAlertService = inject(FeedbackAlertService);
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);

  private readonly _storedReportData =
    signal<RolloutProgressReportEntities.v3.ReportResponse>(
      {} as RolloutProgressReportEntities.v3.ReportResponse,
    );
  storedReportData = computed(() => this._storedReportData());

  private readonly _storedReportsByMarkerType =
    signal<RolloutProgressReportEntities.v3.ReportsByMarkerType>(
      {} as RolloutProgressReportEntities.v3.ReportsByMarkerType,
    );

  readonly storedV3Reports: Signal<RolloutProgressReportEntities.v3.ReportsByMarkerType> =
    this._storedReportsByMarkerType.asReadonly();

  readonly reportId = signal<string>('');

  getReport$ = (
    ifs: string,
    pointType: RolloutProgressReportEntities.v3.MarkerTypes,
    markerId: string,
    options?: Partial<
      QueryOptions<
        RolloutProgressReportEntities.v3.ReportResponse,
        HttpErrorResponse,
        never,
        RolloutProgressReportEntities.v3.ReportResponse
      >
    >,
  ) =>
    useQuery(
      [rpReportKey, markerId],
      () => this.apiService.getReport(ifs, pointType, markerId),
      {
        staleTime: 0,
        cacheTime: 0,
        onSuccess: (data: RolloutProgressReportEntities.v3.ReportResponse) => {
          this.setStoredReportData(data);
        },
        onError: () => console.error('error'),
        ...options,
      },
    )
      .getState$()
      .pipe(
        tap((reportData) => {
          this.setStoredReportIdByQueryData(reportData);
        }),
      );

  getReportStatus$ = (
    ifs: string,
    pointType: RolloutProgressReportEntities.v3.MarkerTypes,
    markerId: string,
  ) =>
    this.getReport$(ifs, pointType, markerId).pipe(
      map(
        (state) =>
          state.data?.status ??
          RolloutProgressReportEntities.v3.ReportStatus.UNKNOWN,
      ),
    );

  getV3ReportsByIfs$ = (
    ifs: string,
    options?: Partial<
      QueryOptions<
        RolloutProgressReportEntities.v3.ReportResponse[],
        HttpErrorResponse,
        never,
        RolloutProgressReportEntities.v3.ReportResponse[]
      >
    >,
  ) =>
    useQuery([rpReportsKey, ifs], () => this.apiService.getV3Reports(ifs), {
      onSuccess: (reports) => {
        this.setStoredV3ReportsByMarkerType(reports);
      },
      onError: (err) => console.error(err),
      staleTime: 0,
      cacheTime: 0,
      ...options,
    }).getState$();

  createV3Report$ = (
    requestBody: RolloutProgressReportEntities.v3.ReportRequestWithSignal,
  ): Mutation<
    RolloutProgressReportEntities.v3.ReportResponse,
    [],
    HttpErrorResponse,
    void
  > =>
    useMutation(
      () =>
        this.apiService.createV3Report({
          ...requestBody,
          spanIds: requestBody.spanIds(),
        }),
      {
        onSuccess: (reportResponse) => {
          this.handleReportMutationOnSuccess(reportResponse, true);
          this.analyticsService.logEvent(MULTI_SPAN_REPORT_CREATED, {
            spans: reportResponse.spans?.length,
          });
        },
        onError: (err) => {
          console.error(err);
          this.errorAlertReportMutation(err);
        },
      },
    );

  updateV3ReportSpans$ = (spanIds: Signal<string[]>) =>
    useMutation(
      () => this.apiService.updateV3ReportSpans(this.reportId(), spanIds()),
      {
        onSuccess: (reportResponse) => {
          this.handleUpdatedReportResponse(spanIds, reportResponse);
          this.handleReportMutationOnSuccess(reportResponse, false);
          this.setStoredReportId('');
          this.analyticsService.logEvent(MULTI_SPAN_REPORT_UPDATED, {
            spans: reportResponse.spans?.length,
          });
        },
        onError: (err) => {
          console.error(err);
          this.errorAlertReportMutation(err);
        },
      },
    );

  getReportWithInitValues = (): void => {
    this.getReport$(
      this.initService.getInitValueProperty('ifs'),
      this.initService.getInitValueProperty('typeId'),
      this.initService.getInitValueProperty('markerId'),
    );
  };

  setStoredReportIdByQueryData = (
    reportData: QueryLoadingState<
      RolloutProgressReportEntities.v3.ReportResponse,
      HttpErrorResponse,
      RolloutProgressReportEntities.v3.ReportResponse
    >,
  ): void => {
    if (reportData.data) {
      this.reportId.set(reportData.data?.id);
    }
  };

  setStoredReportId = (reportId: string): void => {
    this.reportId.set(reportId);
  };

  getStoredReportId = (): string => this.reportId();

  setStoredReportData = (
    report: RolloutProgressReportEntities.v3.ReportResponse,
  ): void => {
    this._storedReportData.set(report);
  };

  setStoredV3ReportsByMarkerType = (
    reports: RolloutProgressReportEntities.v3.ReportResponse[],
  ): void => {
    const reportByMarkerType: RolloutProgressReportEntities.v3.ReportsByMarkerType =
      {} as RolloutProgressReportEntities.v3.ReportsByMarkerType;

    reports.forEach((report) => {
      if (!reportByMarkerType[report.type]) {
        reportByMarkerType[report.type] = {};
      }
      reportByMarkerType[report.type][report.markerId] = report;
    });

    this._storedReportsByMarkerType.set(reportByMarkerType);
  };

  getStoredV3ReportsByMarkerType = (
    markerType: RolloutProgressReportEntities.v3.MarkerTypes,
  ): Record<string, RolloutProgressReportEntities.v3.ReportResponse> =>
    this._storedReportsByMarkerType()[markerType];

  handleReportMutationOnSuccess = (
    reportResponse: RolloutProgressReportEntities.v3.ReportResponse,
    redirect: boolean,
  ): void => {
    this.closeSelectSpanMode();
    this.feedbackAlertService.showAlert(
      'success',
      'feedbackAlert.save.success',
    );

    if (redirect) {
      const spanId = (reportResponse.spans as string[])[0];
      window.open(
        buildRolloutReportUrl(
          this.router.url,
          RolloutProgressReportEntities.v3.MarkerTypes.SPAN,
          spanId,
        ),
        '_self',
      );
    }
  };

  errorAlertReportMutation = (error: HttpErrorResponse): void => {
    error.status === 409
      ? this.feedbackAlertService.showAlert(
          'critical',
          'feedbackAlert.save.spanAlreadyPartOfReport',
        )
      : this.feedbackAlertService.showAlert(
          'critical',
          'feedbackAlert.save.error',
        );
  };

  closeSelectSpanMode(): void {
    this.selectSpanModeService.setSelectSpanMode(false);
  }

  handleUpdatedReportResponse = (
    spanIds: Signal<string[]>,
    reportResponse: RolloutProgressReportEntities.v3.ReportResponse,
  ) => {
    if (
      reportResponse.type === RolloutProgressReportEntities.v3.MarkerTypes.SPAN
    ) {
      const { spanIdsToRemove, spanIdsToAdd, untouchedSpanIds } =
        this.getStoredV3ReportsToUpdate(spanIds);

      this.removeStoredReportsBySpanIds(spanIdsToRemove);
      this.addExplodedReportForNewSpanIds(spanIdsToAdd, reportResponse);
      this.updateStoredReportsBySpanIds(untouchedSpanIds, spanIds);
    }
  };

  getStoredV3ReportsToUpdate = (
    spanIds: Signal<string[]>,
  ): {
    spanIdsToAdd: string[];
    spanIdsToRemove: string[];
    untouchedSpanIds: string[];
  } => {
    const originalSpanIdsSet = new Set(
      this.selectSpanModeService.originalReportSpanIds(),
    );
    const newSpanIdsSet = new Set(spanIds());

    const spanIdsToRemove = this.selectSpanModeService
      .originalReportSpanIds()
      .filter((id) => !newSpanIdsSet.has(id));

    const spanIdsToAdd = spanIds().filter((id) => !originalSpanIdsSet.has(id));
    const untouchedSpanIds = this.selectSpanModeService
      .originalReportSpanIds()
      .filter((id) => newSpanIdsSet.has(id));

    return { spanIdsToRemove, spanIdsToAdd, untouchedSpanIds };
  };

  removeStoredReportsBySpanIds = (spanIdsToRemove: string[]): void => {
    spanIdsToRemove.forEach((id) => {
      delete this._storedReportsByMarkerType()[
        RolloutProgressReportEntities.v3.MarkerTypes.SPAN
      ][id];
    });
  };

  addExplodedReportForNewSpanIds = (
    spanIdsToAdd: string[],
    reportResponse: RolloutProgressReportEntities.v3.ReportResponse,
  ): void => {
    spanIdsToAdd.forEach((id) => {
      this._storedReportsByMarkerType()[
        RolloutProgressReportEntities.v3.MarkerTypes.SPAN
      ] = {
        ...this._storedReportsByMarkerType()[
          RolloutProgressReportEntities.v3.MarkerTypes.SPAN
        ],
        [id]: {
          ...reportResponse,
          markerId: id,
        },
      };
    });
  };

  updateStoredReportsBySpanIds = (
    untouchedSpanIds: string[],
    spanIds: Signal<string[]>,
  ): void => {
    untouchedSpanIds.forEach((id) => {
      this._storedReportsByMarkerType()[
        RolloutProgressReportEntities.v3.MarkerTypes.SPAN
      ][id] = {
        ...this._storedReportsByMarkerType()[
          RolloutProgressReportEntities.v3.MarkerTypes.SPAN
        ][id],
        spans: spanIds(),
      };
    });
  };
}
